import { useState, useRef, useEffect, useReducer } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createReview, fetchJobForReview, fetchExistingReview, fetchReviewerProfile } from '../api/reviewApi';
import { supabase } from '../lib/supabase';
import { tokens } from '../tokens';
import AppLogo from '../components/AppLogo';
import { useBreakpoint } from '../hooks/useBreakpoint';

/* ─── Fonts ─── */
if (!document.getElementById("__rv_fonts")) {
  const l = document.createElement("link");
  l.id  = "__rv_fonts"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap";
  document.head.appendChild(l);
}

/* ═══════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════ */
const T = {
  /* surfaces */
  bg:         "#F7F8FA",
  bgWarm:     "#F9F6F3",
  card:       "#FFFFFF",
  cardRaised: "#FFFFFF",
  text:       "#111827",
  textSub:    "#4B5563",
  textMuted:  "#9CA3AF",
  textFaint:  "#D1D5DB",
  border:     "#E5E7EB",
  borderSub:  "#F3F4F6",
  borderWarm: "#EDE9E4",
  green:      "#16A34A",
  greenDeep:  "#0F7B3E",
  greenHov:   "#15803D",
  greenTint:  "rgba(22,163,74,0.07)",
  greenBd:    "#BBF7D0",
  greenGlow:  "0 0 0 3px rgba(22,163,74,0.12)",
  gold:       "#D97706",
  goldBright: "#F59E0B",
  goldLight:  "#FFFBEB",
  goldBd:     "#FDE68A",
  goldTint:   "rgba(217,119,6,0.08)",
  error:      "#DC2626",
  errorTint:  "#FEF2F2",
  errorBd:    "#FECACA",
  shadowXs:   "0 1px 3px rgba(0,0,0,0.06)",
  shadowSm:   "0 2px 8px rgba(0,0,0,0.07)",
  shadow:     "0 4px 16px rgba(0,0,0,0.07)",
  shadowMd:   "0 8px 28px rgba(0,0,0,0.10)",
  shadowLg:   "0 16px 48px rgba(0,0,0,0.12)",
  shadowXl:   "0 28px 72px rgba(0,0,0,0.15)",
  imgOverlay: "linear-gradient(to right, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 35%)",
  imgFade:    "linear-gradient(to bottom, rgba(0,0,0,0) 50%, rgba(0,0,0,0.40) 100%)",
  font:     "'Plus Jakarta Sans', system-ui, sans-serif",
  r:        "16px",
  rSm:      "10px",
  rMd:      "13px",
};

/* ═══════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════ */
const RATING_COPY = {
  0: "",
  1: "We're sorry this wasn't a great experience.",
  2: "Thank you for the feedback, what could have been better?",
  3: "Okay, a few things could have gone smoother.",
  4: "Great work. What made it stand out?",
  5: "Excellent, we'd love to hear what impressed you most.",
};

const RATING_LABEL = { 0:"–", 1:"Poor", 2:"Fair", 3:"Good", 4:"Great", 5:"Excellent" };

const CATEGORY_LABELS = [
  "Quality of work",
  "Communication",
  "Punctuality",
  "Professionalism",
  "Value for money",
];

const TIP_OPTIONS = [
  { label:"No tip",  value:0   },
  { label:"KSh 100", value:100 },
  { label:"KSh 200", value:200 },
  { label:"KSh 500", value:500 },
];

const PROMPT_CHIPS = [
  "Arrived on time",
  "Very professional",
  "Great communication",
  "Excellent quality",
  "Cleaned thoroughly",
  "Would book again",
  "Highly recommended",
  "Friendly and kind",
];

const STEPS = [
  "Rate the service",
  "Add details",
  "Upload photos",
  "Submit review",
];

/* ═══════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════ */
const CSS = `
  @keyframes rvFadeUp    { from{opacity:0;transform:translateY(18px);} to{opacity:1;transform:translateY(0);} }
  @keyframes rvFadeIn    { from{opacity:0;} to{opacity:1;} }
  @keyframes rvSlideDown { from{opacity:0;transform:translateY(-10px);} to{opacity:1;transform:translateY(0);} }
  @keyframes rvSpin      { to{transform:rotate(360deg);} }
  @keyframes rvSuccess   { 0%{transform:scale(0.72);opacity:0;} 60%{transform:scale(1.08);} 100%{transform:scale(1);opacity:1;} }
  @keyframes rvStarPop   { 0%{transform:scale(1);} 35%{transform:scale(1.32);} 100%{transform:scale(1);} }
  @keyframes rvPulse     { 0%,100%{opacity:1;} 50%{opacity:0.38;} }
  @keyframes rvShimmer   { from{background-position:-400px 0;} to{background-position:400px 0;} }

  *, *::before, *::after { box-sizing:border-box; }
  html { scroll-behavior:smooth; }
  body { margin:0; background:${T.bg}; font-family:${T.font}; color:${T.text}; -webkit-font-smoothing:antialiased; }

  ::-webkit-scrollbar       { width:5px; height:5px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:#D1D5DB; border-radius:99px; }
  ::-webkit-scrollbar-thumb:hover { background:#9CA3AF; }

  textarea, input { font-family:${T.font}; }
  textarea { resize:none; }
  input::placeholder, textarea::placeholder { color:${T.textMuted}; }

  .rv-surface {
    background:${T.card};
    border:1px solid ${T.border};
    border-radius:${T.r};
    box-shadow:${T.shadow};
  }
  .rv-surface-raised {
    background:${T.cardRaised};
    border:1px solid ${T.border};
    border-radius:${T.r};
    box-shadow:${T.shadowMd};
  }
  .rv-surface-warm {
    background:${T.bgWarm};
    border:1px solid ${T.borderWarm};
    border-radius:${T.r};
    box-shadow:${T.shadowXs};
  }

  .rv-lift {
    transition:transform 0.22s ease, box-shadow 0.22s ease;
    cursor:default;
  }
  .rv-lift:hover { transform:translateY(-2px); box-shadow:${T.shadowMd}; }

  .rv-lift-sm {
    transition:transform 0.18s ease, box-shadow 0.18s ease;
  }
  .rv-lift-sm:hover { transform:translateY(-1px); box-shadow:${T.shadowSm}; }

  .rv-btn-primary {
    display:inline-flex; align-items:center; justify-content:center; gap:8px;
    background:${T.green}; color:#fff; border:none;
    border-radius:12px; font-family:${T.font}; font-weight:700; font-size:14px;
    cursor:pointer; letter-spacing:-0.01em;
    transition:background 0.18s, transform 0.15s, box-shadow 0.18s;
    box-shadow:0 4px 14px rgba(22,163,74,0.28);
  }
  .rv-btn-primary:hover:not(:disabled)  { background:${T.greenHov}; transform:translateY(-1.5px); box-shadow:0 6px 20px rgba(22,163,74,0.36); }
  .rv-btn-primary:active:not(:disabled) { background:#166534; transform:scale(0.97) translateY(0); box-shadow:0 2px 6px rgba(22,163,74,0.22); }
  .rv-btn-primary:disabled { opacity:0.48; cursor:default; transform:none; box-shadow:none; }
  .rv-btn-primary:focus-visible { outline:none; box-shadow:0 0 0 3px rgba(22,163,74,0.30); }

  .rv-btn-ghost {
    display:inline-flex; align-items:center; justify-content:center; gap:7px;
    background:none; color:${T.textSub}; border:1.5px solid ${T.border};
    border-radius:12px; font-family:${T.font}; font-weight:600; font-size:13px;
    cursor:pointer; transition:all 0.18s;
  }
  .rv-btn-ghost:hover  { border-color:${T.green}; color:${T.green}; background:${T.greenTint}; transform:translateY(-1px); }
  .rv-btn-ghost:active { transform:scale(0.97) translateY(0); }

  .chip {
    display:inline-flex; align-items:center; gap:5px;
    padding:6px 14px; border-radius:99px; font-size:12px; font-weight:600;
    cursor:pointer; border:1.5px solid ${T.border}; background:${T.card};
    color:${T.textSub}; font-family:${T.font};
    transition:all 0.17s; user-select:none; white-space:nowrap;
  }
  .chip:hover         { border-color:#9CA3AF; color:${T.text}; background:#F9FAFB; transform:translateY(-1px); }
  .chip:active        { transform:scale(0.95) translateY(0); }
  .chip.chip--active  { border-color:${T.green}; color:${T.green}; background:${T.greenTint}; }

  .nav-link {
    font-size:14px; font-weight:500; color:${T.textSub};
    background:none; border:none; border-bottom:2px solid transparent;
    padding:6px 0; cursor:pointer; font-family:${T.font};
    transition:color 0.15s, border-color 0.15s; white-space:nowrap;
  }
  .nav-link:hover { color:${T.text}; border-bottom-color:${T.border}; }

  .step-node {
    width:34px; height:34px; border-radius:50%;
    display:flex; align-items:center; justify-content:center;
    font-size:12px; font-weight:700; font-family:${T.font};
    flex-shrink:0; transition:all 0.28s ease;
  }

  .upload-zone {
    border:2px dashed ${T.border}; border-radius:${T.r};
    transition:border-color 0.2s, background 0.2s; cursor:pointer;
    background:${T.bg};
  }
  .upload-zone:hover    { border-color:#9CA3AF; background:#F9FAFB; }
  .upload-zone.dragging { border-color:${T.green}; background:${T.greenTint}; }

  .sub-star {
    cursor:pointer;
    transition:transform 0.12s;
    flex-shrink:0;
  }
  .sub-star:hover { transform:scale(1.18); }

  .tip-pill {
    padding:8px 20px; border-radius:99px;
    border:1.5px solid ${T.border};
    background:${T.card}; color:${T.textSub};
    font-size:13px; font-weight:600;
    cursor:pointer; font-family:${T.font};
    transition:all 0.17s;
  }
  .tip-pill:hover:not(.tip-pill--active)  { border-color:#9CA3AF; color:${T.text}; }
  .tip-pill.tip-pill--active {
    border-color:${T.gold};
    color:${T.gold};
    background:${T.goldLight};
    box-shadow:0 2px 10px rgba(217,119,6,0.18);
  }

  .sidebar-action {
    width:100%; padding:10px 14px; border-radius:${T.rSm};
    border:1.5px solid ${T.border}; background:${T.card};
    color:${T.textSub}; font-size:12px; font-weight:600;
    cursor:pointer; font-family:${T.font};
    text-align:left; display:flex; align-items:center; gap:9px;
    transition:all 0.17s;
  }
  .sidebar-action:hover { border-color:${T.green}; color:${T.green}; background:${T.greenTint}; transform:translateX(2px); }

  .guide-card {
    background:${T.card}; border:1px solid ${T.border};
    border-radius:${T.r}; overflow:hidden;
    transition:transform 0.22s ease, box-shadow 0.22s ease;
  }
  .guide-card:hover { transform:translateY(-3px); box-shadow:${T.shadowMd}; }

  .skeleton {
    background:linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%);
    background-size:400px 100%;
    animation:rvShimmer 1.4s infinite linear;
    border-radius:6px;
  }

  .dropdown { animation:rvSlideDown 0.18s cubic-bezier(0.22,1,0.36,1) both; }

  .rv-img { opacity:0; transition:opacity 0.4s ease; }
  .rv-img.loaded { opacity:1; }

  .star-zone--glow {
    padding:16px 20px; border-radius:${T.rMd};
    background:${T.goldLight};
    border:1px solid ${T.goldBd};
    transition:all 0.3s ease;
  }
  .star-zone--idle {
    padding:16px 20px; border-radius:${T.rMd};
    background:#FAFBFA;
    border:1px solid ${T.border};
    transition:all 0.3s ease;
  }

  .rv-divider { height:1px; background:${T.borderSub}; }

  .verified-badge {
    display:inline-flex; align-items:center; gap:6px;
    padding:5px 12px; border-radius:99px;
    background:${T.greenTint}; border:1px solid ${T.greenBd};
    font-size:11px; font-weight:700; color:${T.green};
    font-family:${T.font};
  }

  .pro-reply-card {
    background:${T.bgWarm}; border:1px solid ${T.borderWarm};
    border-radius:${T.rMd}; padding:14px 16px;
    margin-top:14px; margin-left: 24px;
    animation:rvFadeIn 0.4s ease both;
  }
`;

/* ═══════════════════════════════════════════════
   SMALL ATOMS
═══════════════════════════════════════════════ */
function Icon({ d, d2, size = 16, stroke = "currentColor", fill = "none", sw = 2, circle }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke}
      strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
      <path d={d}/>
      {d2 && <path d={d2}/>}
      {circle && <circle cx={circle[0]} cy={circle[1]} r={circle[2]}/>}
    </svg>
  );
}

function Check({ size = 14, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill={color} style={{ flexShrink:0 }}>
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
    </svg>
  );
}

function StarSvg({ filled, half, size = 36, color }) {
  const c = color || T.goldBright;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={filled ? c : "none"}
      stroke={filled ? c : T.textFaint}
      strokeWidth="1.5"
      style={{ display:"block", flexShrink:0 }}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  );
}

function RvImg({ src, alt, style, fallbackBg, fallbackContent }) {
  const ref = useRef(null);
  const [loaded, setLoaded]   = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => { setLoaded(false); setErrored(false); }, [src]);

  if (!src || errored) {
    return (
      <div style={{
        width:"100%", height:"100%",
        background: fallbackBg || "linear-gradient(145deg,#f0fdf4,#dcfce7,#bbf7d0)",
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:56, ...style,
      }}>
        {fallbackContent || "🏡"}
      </div>
    );
  }

  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      className={`rv-img${loaded ? " loaded" : ""}`}
      style={{ display:"block", ...style }}
      onLoad={() => setLoaded(true)}
      onError={() => setErrored(true)}
    />
  );
}

/* ═══════════════════════════════════════════════
   VERIFIED BOOKING BADGE
   Shown on ReviewPreview only when the job_request is completed
   and belongs to the logged-in reviewer.
═══════════════════════════════════════════════ */
function VerifiedBadge() {
  return (
    <span className="verified-badge">
      <Check size={10} color={T.green}/>
      Verified Booking
    </span>
  );
}

/* ═══════════════════════════════════════════════
   PRO REPLY BLOCK
   Read-only display of review.pro_reply, if present.
   Submission of replies happens on the Pro side (not built here).
═══════════════════════════════════════════════ */
function ProReplyBlock({ reply, replyAt, proName }) {
  const { isMobile } = useBreakpoint();
  if (!reply) return null;

  const formattedDate = replyAt
    ? new Date(replyAt).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" })
    : null;

  return (
    <div className="pro-reply-card">
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
        <div style={{
          width:22, height:22, borderRadius:"50%",
          background:T.greenTint, border:`1px solid ${T.greenBd}`,
          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
        }}>
          <Icon d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" size={11} stroke={T.green} sw={2.2}/>
        </div>
        <p style={{ fontSize: isMobile ? 11 : 12, fontWeight:700, color:T.text, margin:0, fontFamily:T.font }}>
          Response from {proName || "the JiraniPro"}
        </p>
        {formattedDate && (
          <span style={{ fontSize: isMobile ? 10 : 11, color:T.textMuted, fontFamily:T.font, marginLeft: isMobile ? 0 : "auto" }}>
            {formattedDate}
          </span>
        )}
      </div>
      <p style={{ fontSize: isMobile ? 12 : 13, color:T.textSub, margin:0, fontFamily:T.font, lineHeight:1.65 }}>
        {reply}
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PAGE-LEVEL LOADING SKELETON
   Shown while job_request + reviewer profile are being fetched.
═══════════════════════════════════════════════ */
function PageSkeleton() {
  const { isMobile } = useBreakpoint();
  return (
    <main style={{ maxWidth:1280, margin:"0 auto", padding: isMobile ? "20px 20px 0" : "32px 36px 0" }}>
      <div style={{ maxWidth:1120, margin:"0 auto" }}>
        <div className="rv-surface-raised" style={{
          display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 420px",
          gap:0, overflow:"hidden", marginBottom:28, minHeight: isMobile ? 320 : 360,
        }}>
          <div style={{ padding: isMobile ? "28px 24px" : "48px 48px 44px", display:"flex", flexDirection:"column", gap:18 }}>
            <div className="skeleton" style={{ width:160, height:14 }}/>
            <div className="skeleton" style={{ width:"80%", height:36 }}/>
            <div className="skeleton" style={{ width:"60%", height:16 }}/>
            <div className="skeleton" style={{ width:"100%", height:120, borderRadius:13, marginTop:"auto" }}/>
          </div>
          {!isMobile && <div className="skeleton" style={{ height:"100%", borderRadius:0 }}/>}
        </div>
        <div className="skeleton" style={{ width:"100%", height:60, marginBottom:28 }}/>
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "7fr 5fr", gap:28 }}>
          <div className="skeleton" style={{ width:"100%", height:480 }}/>
          {!isMobile && <div className="skeleton" style={{ width:"100%", height:480 }}/>}
        </div>
      </div>
    </main>
  );
}

/* ═══════════════════════════════════════════════
   PAGE-LEVEL ERROR STATE
   Shown if the job_request fetch fails (not found, no access, etc.)
═══════════════════════════════════════════════ */
function PageError({ message, onRetry, onBack }) {
  const { isMobile } = useBreakpoint();
  return (
    <main style={{ maxWidth:1280, margin:"0 auto", padding: isMobile ? "20px 20px 0" : "32px 36px 0" }}>
      <div style={{ maxWidth:560, margin:"60px auto" }}>
        <div className="rv-surface-raised" style={{ padding: isMobile ? "32px 24px" : "48px 44px", textAlign:"center" }}>
          <div style={{
            width:56, height:56, borderRadius:16, margin:"0 auto 20px",
            background:T.errorTint, border:`1px solid ${T.errorBd}`,
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>
            <Icon d="M12 8v4M12 16h.01" d2="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" size={24} stroke={T.error} sw={2}/>
          </div>
          <h2 style={{ fontSize: isMobile ? 18 : 20, fontWeight:800, color:T.text, margin:"0 0 8px", fontFamily:T.font, letterSpacing:"-0.02em" }}>
            We couldn't load this booking
          </h2>
          <p style={{ fontSize: isMobile ? 13 : 14, color:T.textSub, margin:"0 0 24px", fontFamily:T.font, lineHeight:1.6 }}>
            {message || "This booking may not exist, or you may not have permission to review it."}
          </p>
          <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
            {onRetry && (
              <button className="rv-btn-primary" onClick={onRetry} style={{ height:46, padding:"0 28px", fontSize:14 }}>
                Try again
              </button>
            )}
            <button className="rv-btn-ghost" onClick={onBack} style={{ height:46, padding:"0 24px" }}>
              Back to bookings
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

/* ═══════════════════════════════════════════════
   1. NAVBAR — revised
   - AppLogo replaces inline wordmark
   - Center nav links removed
   - Notifications + Messages dropdown shells (UI-only placeholders;
     live data wiring comes once `notifications`/`conversations` exist)
   - Profile avatar from authenticated profile, fallback to initials
   - Dropdown trimmed to: My Bookings, Help & Support, Logout
═══════════════════════════════════════════════ */
function Navbar({ reviewer }) {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen]     = useState(false);
  const [msgOpen, setMsgOpen]         = useState(false);
  const [scrolled, setScrolled]       = useState(false);
  const profileRef = useRef(null);
  const notifRef   = useRef(null);
  const msgRef     = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    const onDown   = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (msgRef.current && !msgRef.current.contains(e.target)) setMsgOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") { setProfileOpen(false); setNotifOpen(false); setMsgOpen(false); }
    };
    window.addEventListener("scroll", onScroll, { passive:true });
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const handleSignOut = () => navigate('/');

  const navPadding  = isMobile ? "0 20px" : "0 36px";
  const avatarSize  = isMobile ? 34 : 38;

  const displayName = reviewer?.full_name || reviewer?.email?.split('@')[0] || "Account";
  const initials    = displayName.trim().charAt(0).toUpperCase() || "A";

  return (
    <header style={{
      position:"sticky", top:0, zIndex:400,
      height: isMobile ? 56 : 64,
      background: scrolled ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.92)",
      backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
      borderBottom:`1px solid ${scrolled ? T.border : "rgba(229,231,235,0.5)"}`,
      display:"flex", alignItems:"center", justifyContent:"space-between",
      padding: navPadding,
      transition:"box-shadow 0.2s, border-color 0.2s",
      boxShadow: scrolled ? T.shadowXs : "none",
    }}>
      <AppLogo
        size={isMobile ? 16 : 18}
        onClick={() => navigate("/")}
        style={{ minHeight: isMobile ? tokens.spacing[44] : "auto" }}
      />

      <div style={{ display:"flex", alignItems:"center", gap: isMobile ? 2 : 4 }}>
        {/* Notifications — UI shell, live data in a later phase */}
        <div ref={notifRef} style={{ position:"relative" }}>
          <NavIcon tip="Notifications" badge onClick={() => { setNotifOpen(o => !o); setMsgOpen(false); }}>
            <Icon d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" d2="M13.73 21a2 2 0 0 1-3.46 0" size={isMobile ? 16 : 18}/>
          </NavIcon>
          {notifOpen && (
            <div className="dropdown rv-surface" style={{
              position:"absolute", right:0, top:"calc(100% + 10px)",
              width: isMobile ? 240 : 280, overflow:"hidden",
              boxShadow:T.shadowXl, zIndex:500,
            }}>
              <div style={{ padding:"14px 16px", borderBottom:`1px solid ${T.borderSub}` }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.text, margin:0, fontFamily:T.font }}>Notifications</p>
              </div>
              <div style={{ padding:"28px 16px", textAlign:"center" }}>
                <p style={{ fontSize:12, color:T.textMuted, margin:0, fontFamily:T.font }}>You're all caught up.</p>
              </div>
              <div style={{ borderTop:`1px solid ${T.borderSub}`, padding:"10px 16px" }}>
                <button onClick={() => { setNotifOpen(false); navigate("/notifications"); }} style={{
                  width:"100%", background:"none", border:"none", color:T.green,
                  fontSize:12, fontWeight:700, fontFamily:T.font, cursor:"pointer", padding:0,
                  minHeight: isMobile ? tokens.spacing[44] : "auto",
                }}>
                  View all
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Messages — UI shell, live data in a later phase */}
        <div ref={msgRef} style={{ position:"relative" }}>
          <NavIcon tip="Messages" onClick={() => { setMsgOpen(o => !o); setNotifOpen(false); }}>
            <Icon d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" size={isMobile ? 16 : 18}/>
          </NavIcon>
          {msgOpen && (
            <div className="dropdown rv-surface" style={{
              position:"absolute", right:0, top:"calc(100% + 10px)",
              width: isMobile ? 240 : 280, overflow:"hidden",
              boxShadow:T.shadowXl, zIndex:500,
            }}>
              <div style={{ padding:"14px 16px", borderBottom:`1px solid ${T.borderSub}` }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.text, margin:0, fontFamily:T.font }}>Messages</p>
              </div>
              <div style={{ padding:"28px 16px", textAlign:"center" }}>
                <p style={{ fontSize:12, color:T.textMuted, margin:0, fontFamily:T.font }}>No new messages.</p>
              </div>
              <div style={{ borderTop:`1px solid ${T.borderSub}`, padding:"10px 16px" }}>
                <button onClick={() => { setMsgOpen(false); navigate("/messages"); }} style={{
                  width:"100%", background:"none", border:"none", color:T.green,
                  fontSize:12, fontWeight:700, fontFamily:T.font, cursor:"pointer", padding:0,
                  minHeight: isMobile ? tokens.spacing[44] : "auto",
                }}>
                  Open inbox
                </button>
              </div>
            </div>
          )}
        </div>

        <div ref={profileRef} style={{ position:"relative", marginLeft: isMobile ? 2 : 6 }}>
          <button
            onClick={() => { setProfileOpen(o => !o); setNotifOpen(false); setMsgOpen(false); }}
            style={{
              width: avatarSize, height: avatarSize, borderRadius:"50%", overflow:"hidden",
              border:`2.5px solid ${profileOpen ? T.green : T.border}`,
              cursor:"pointer", padding:0, background:"none",
              transition:"border-color 0.15s, box-shadow 0.15s",
              boxShadow: profileOpen ? `0 0 0 3px rgba(22,163,74,0.18)` : "none",
              minHeight: isMobile ? tokens.spacing[44] : "auto",
            }}>
            <RvImg
              src={reviewer?.avatar_url}
              alt="Profile"
              style={{ width:"100%", height:"100%", objectFit:"cover" }}
              fallbackBg="linear-gradient(135deg,#a7f3d0,#16a34a)"
              fallbackContent={<span style={{ fontSize: isMobile ? 13 : 15, fontWeight:700, color:"#fff" }}>{initials}</span>}
            />
          </button>

          {profileOpen && (
            <div className="dropdown rv-surface" style={{
              position:"absolute", right:0, top:"calc(100% + 10px)",
              width: isMobile ? 180 : 210, overflow:"hidden",
              boxShadow:T.shadowXl, zIndex:500,
            }}>
              <div style={{ padding:"14px 16px 10px", borderBottom:`1px solid ${T.borderSub}` }}>
                <p style={{ fontSize: isMobile ? 12 : 13, fontWeight:700, color:T.text, margin:"0 0 1px", fontFamily:T.font }}>{displayName}</p>
                {reviewer?.email && (
                  <p style={{ fontSize: isMobile ? 10 : 11, color:T.textMuted, margin:0, fontFamily:T.font }}>{reviewer.email}</p>
                )}
              </div>
              <DropRow d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" label="My Bookings" onClick={() => { setProfileOpen(false); navigate("/bookings"); }}/>
              <DropRow d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" d2="M12 9v4M12 17h.01" label="Help &amp; Support" onClick={() => { setProfileOpen(false); navigate("/help"); }}/>
              <div style={{ height:1, background:T.borderSub }}/>
              <DropRow d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" d2="M16 17l5-5-5-5M21 12H9" label="Sign out" danger onClick={() => { setProfileOpen(false); handleSignOut(); }}/>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function NavIcon({ children, tip, badge, onClick }) {
  const [h, setH] = useState(false);
  const { isMobile } = useBreakpoint();
  return (
    <button title={tip} onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        position:"relative", width: isMobile ? 34 : 38, height: isMobile ? 34 : 38, borderRadius:10, border:"none",
        background: h ? "#F3F4F6" : "none",
        color: h ? T.text : T.textSub,
        display:"flex", alignItems:"center", justifyContent:"center",
        cursor:"pointer", transition:"all 0.15s",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
      }}>
      {children}
      {badge && (
        <span style={{
          position:"absolute", top:7, right:7,
          width:7, height:7, borderRadius:"50%",
          background:T.error, border:"2px solid #fff",
        }}/>
      )}
    </button>
  );
}

function DropRow({ d, d2, c, label, danger, onClick }) {
  const [h, setH] = useState(false);
  const { isMobile } = useBreakpoint();
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        width:"100%", background: h ? (danger ? T.errorTint : "#F9FAFB") : "none",
        border:"none", padding: isMobile ? "8px 14px" : "11px 16px", textAlign:"left",
        display:"flex", alignItems:"center", gap:11,
        cursor:"pointer", transition:"background 0.14s",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
      }}>
      <svg width={isMobile ? 12 : 14} height={isMobile ? 12 : 14} viewBox="0 0 24 24" fill="none"
        stroke={danger ? T.error : T.textSub} strokeWidth="2" strokeLinecap="round">
        <path d={d}/>{d2 && <path d={d2}/>}{c && <circle cx="12" cy="7" r="4"/>}
      </svg>
      <span style={{ fontSize: isMobile ? 12 : 13, fontWeight:500, color: danger ? T.error : T.text, fontFamily:T.font }}>{label}</span>
    </button>
  );
}

/* ═══════════════════════════════════════════════
   2. HERO SECTION — revised
   - Pro name/photo from job.pro_profiles
   - Booking summary from job_requests (description, location,
     scheduled_time, estimated_price) — service name comes from
     job.description (no FK to `services`, per known data gap)
═══════════════════════════════════════════════ */
function Hero({ job }) {
  const { isMobile } = useBreakpoint();
  const gridColumns = isMobile ? "1fr" : "1fr 420px";
  const leftPadding = isMobile ? "28px 24px" : "48px 48px 44px";
  const titleFontSize = isMobile ? 28 : 36;

  const pro = job?.pro_profiles || {};
  const proName  = pro.full_name || "Your JiraniPro";
  const proInitial = proName.trim().charAt(0).toUpperCase() || "P";
  const serviceLabel = job?.description || "Service";
  const categoryLabel = job?.categories?.name;

  const formattedDate = job?.scheduled_time
    ? new Date(job.scheduled_time).toLocaleString("en-GB", {
        weekday:"short", day:"numeric", month:"short", year:"numeric", hour:"numeric", minute:"2-digit",
      })
    : "—";

  const formattedPrice = job?.estimated_price != null
    ? `KSh ${Number(job.estimated_price).toLocaleString()}`
    : "—";

  const isCompleted = job?.status === "completed";

  return (
    <div style={{ animation:"rvFadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both" }}>
      <div className="rv-surface-raised" style={{
        display:"grid", gridTemplateColumns: gridColumns,
        gap:0, overflow:"hidden", marginBottom:28,
        boxShadow:T.shadowLg,
      }}>
        <div style={{ padding: leftPadding, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:28, flexWrap:"wrap" }}>
            <div style={{
              width: isMobile ? 40 : 48, height: isMobile ? 40 : 48, borderRadius:"50%", overflow:"hidden",
              flexShrink:0, border:`2px solid ${T.border}`,
              boxShadow:T.shadowXs,
            }}>
              <RvImg
                src={pro.avatar_url}
                alt={proName}
                style={{ width:"100%", height:"100%", objectFit:"cover" }}
                fallbackBg="linear-gradient(145deg,#d1fae5,#16a34a)"
                fallbackContent={<span style={{ fontSize: isMobile ? 16 : 18, fontWeight:700, color:"#fff" }}>{proInitial}</span>}
              />
            </div>
            <div>
              <p style={{ fontSize: isMobile ? 10 : 11, fontWeight:700, color:T.textMuted, margin:"0 0 1px", fontFamily:T.font, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                Completed booking
              </p>
              <p style={{ fontSize: isMobile ? 14 : 15, fontWeight:700, color:T.text, margin:0, fontFamily:T.font }}>
                {proName}{categoryLabel ? ` · ${categoryLabel}` : ""}
              </p>
            </div>
            <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:6,
              padding:"5px 11px", borderRadius:20,
              background: isCompleted ? T.greenTint : "#F3F4F6",
              border:`1px solid ${isCompleted ? T.greenBd : T.border}` }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background: isCompleted ? T.green : T.textMuted, display:"block" }}/>
              <span style={{ fontSize: isMobile ? 10 : 11, fontWeight:700, color: isCompleted ? T.green : T.textMuted, fontFamily:T.font, textTransform:"capitalize" }}>
                {job?.status || "Unknown"}
              </span>
            </div>
          </div>

          <div style={{ marginBottom:20 }}>
            <h1 style={{
              fontSize: titleFontSize, fontWeight:800, color:T.text,
              margin:"0 0 12px", fontFamily:T.font,
              letterSpacing:"-0.03em", lineHeight:1.08,
            }}>
              How was your<br/>experience with {proName.split(" ")[0]}?
            </h1>
            {!isMobile && (
              <p style={{ fontSize:15, color:T.textSub, margin:"0 0 6px", fontFamily:T.font, lineHeight:1.65, maxWidth:420 }}>
                Your feedback helps future clients choose the right JiraniPro and rewards pros who do great work.
              </p>
            )}
            <p style={{ fontSize: isMobile ? 12 : 13, color:T.textMuted, margin:0, fontFamily:T.font, fontStyle:"italic" }}>
              Reviews are published after moderation.
            </p>
          </div>

          <div className="rv-surface-warm" style={{ padding: isMobile ? "14px 16px" : "18px 20px" }}>
            <p style={{ fontSize: isMobile ? 9 : 10, fontWeight:700, color:T.textMuted, margin:"0 0 14px", fontFamily:T.font, textTransform:"uppercase", letterSpacing:"0.09em" }}>
              Booking summary
            </p>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", rowGap:12, columnGap:28 }}>
              {[
                ["Service",  serviceLabel],
                ["Date",     formattedDate],
                ["Location", job?.location || "—"],
                ["Price",    formattedPrice],
              ].map(([k, v]) => (
                <div key={k}>
                  <p style={{ fontSize: isMobile ? 9 : 10, color:T.textMuted, margin:"0 0 2px", fontFamily:T.font, textTransform:"uppercase", letterSpacing:"0.06em" }}>{k}</p>
                  <p style={{ fontSize: isMobile ? 12 : 13, fontWeight:700, color:T.text, margin:0, fontFamily:T.font }}>{v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {!isMobile && (
          <div style={{ position:"relative", overflow:"hidden", minHeight:360, background:T.bgWarm }}>
            <RvImg
              src="/images/Reviews/review-hero-1.png"
              alt="Completed service"
              style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
              fallbackBg="linear-gradient(155deg,#f9f6f3 0%,#e7ede8 50%,#c8dac8 100%)"
              fallbackContent="🏡"
            />
            <div style={{ position:"absolute", inset:0, background:T.imgOverlay, pointerEvents:"none" }}/>
            <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"28px 22px 20px",
              background:"linear-gradient(to top, rgba(0,0,0,0.52) 0%, rgba(0,0,0,0) 100%)" }}>
              <p style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.85)", margin:0, fontFamily:T.font }}>
                {serviceLabel}{job?.location ? ` · ${job.location}` : ""}
              </p>
            </div>
            <div style={{
              position:"absolute", top:20, right:18,
              width:96, height:96, borderRadius:12,
              overflow:"hidden", border:"3px solid rgba(255,255,255,0.85)",
              boxShadow:T.shadowMd,
              animation:"rvFadeIn 0.6s ease 0.3s both",
            }}>
              <RvImg
                src="/images/Reviews/review-hero-2.png"
                alt=""
                style={{ width:"100%", height:"100%", objectFit:"cover" }}
                fallbackBg="#e0e7ff"
                fallbackContent="🪣"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   3. PROGRESS INDICATOR
═══════════════════════════════════════════════ */
function ProgressBar({ current }) {
  const { isMobile } = useBreakpoint();
  const stepFontSize = isMobile ? 10 : 11;
  const stepNodeSize = isMobile ? 30 : 34;

  return (
    <div style={{ display:"flex", alignItems:"center", marginBottom:28, animation:"rvFadeUp 0.5s ease 0.07s both", flexWrap: "wrap" }}>
      {STEPS.map((label, i) => {
        const done   = i < current;
        const active = i === current;
        return (
          <div key={i} style={{ display:"flex", alignItems:"center", flex: i < STEPS.length - 1 ? 1 : 0, minWidth: isMobile ? 70 : "auto" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:7 }}>
              <div className="step-node" style={{
                width: stepNodeSize, height: stepNodeSize, borderRadius:"50%",
                background:   done ? T.green : active ? T.text : "#F3F4F6",
                color:        done || active ? "#fff" : T.textMuted,
                border:       active ? `2.5px solid ${T.green}` : "2.5px solid transparent",
                boxShadow:    active ? `0 0 0 4px rgba(22,163,74,0.15)` : "none",
              }}>
                {done ? <Check size={12}/> : <span style={{ fontSize: stepFontSize }}>{i + 1}</span>}
              </div>
              <span style={{
                fontSize: stepFontSize, fontFamily:T.font, whiteSpace:"nowrap",
                fontWeight: active ? 700 : done ? 600 : 500,
                color: active ? T.text : done ? T.green : T.textMuted,
              }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                flex:1, height:2, margin:"0 8px 20px",
                background: done ? T.green : T.border,
                borderRadius:1, transition:"background 0.4s",
              }}/>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   4. STAR RATING
═══════════════════════════════════════════════ */
function StarRating({ rating, onRate }) {
  const [hov, setHov] = useState(0);
  const active = hov > 0 ? hov : rating;
  const { isMobile } = useBreakpoint();
  const starSize = isMobile ? 36 : 42;
  const labelFontSize = isMobile ? 18 : 22;

  return (
    <div className={active > 0 ? "star-zone--glow" : "star-zone--idle"}>
      <div style={{ display:"flex", gap: isMobile ? 6 : 10, marginBottom:14, alignItems:"center", flexWrap:"wrap" }}>
        {[1,2,3,4,5].map(i => {
          const lit = active >= i;
          return (
            <div key={i}
              style={{
                cursor:"pointer",
                transform: lit ? "scale(1.10)" : "scale(1)",
                transition:"transform 0.15s cubic-bezier(0.34,1.56,0.64,1)",
                lineHeight:0,
              }}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(0)}
              onClick={() => { onRate(i); }}>
              <StarSvg filled={lit} size={starSize}/>
            </div>
          );
        })}
        {active > 0 && (
          <span style={{
            marginLeft:6, fontSize: labelFontSize, fontWeight:800, color:T.gold,
            fontFamily:T.font, letterSpacing:"-0.02em",
            animation:"rvFadeIn 0.2s ease both",
          }}>
            {RATING_LABEL[active]}
          </span>
        )}
      </div>
      {active > 0 && (
        <p style={{ fontSize: isMobile ? 12 : 13, color:T.textSub, margin:0, fontFamily:T.font, lineHeight:1.5, fontStyle:"italic", animation:"rvFadeIn 0.25s ease both" }}>
          {RATING_COPY[active]}
        </p>
      )}
      {active === 0 && (
        <p style={{ fontSize: isMobile ? 12 : 13, color:T.textMuted, margin:0, fontFamily:T.font }}>Tap a star to begin your review.</p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   5. CATEGORY RATINGS
═══════════════════════════════════════════════ */
function CategoryRatings({ values, onChange }) {
  const [hovState, setHovState] = useState({});
  const { isMobile } = useBreakpoint();
  const labelFontSize = isMobile ? 12 : 13;
  const starSize = isMobile ? 18 : 20;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
      {CATEGORY_LABELS.map((label, li) => {
        const val = values[label] || 0;
        const hov = hovState[label] || 0;
        const eff = hov > 0 ? hov : val;
        return (
          <div key={label} style={{
            display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"11px 0",
            borderBottom: li < CATEGORY_LABELS.length - 1 ? `1px solid ${T.borderSub}` : "none",
            flexWrap: "wrap",
            gap: 8,
          }}>
            <span style={{ fontSize: labelFontSize, color: val ? T.text : T.textSub, fontFamily:T.font, fontWeight: val ? 600 : 500, minWidth: isMobile ? 120 : 150 }}>
              {label}
            </span>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ display:"flex", gap:4 }}>
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="sub-star"
                    onMouseEnter={() => setHovState(s => ({ ...s, [label]:i }))}
                    onMouseLeave={() => setHovState(s => ({ ...s, [label]:0 }))}
                    onClick={() => onChange(label, i)}>
                    <svg width={starSize} height={starSize} viewBox="0 0 24 24"
                      fill={eff >= i ? T.goldBright : T.borderSub}
                      stroke={eff >= i ? T.goldBright : T.borderSub}
                      strokeWidth="0.5">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  </div>
                ))}
              </div>
              {val > 0 && (
                <span style={{ fontSize: isMobile ? 10 : 11, fontWeight:700, color:T.gold, fontFamily:T.font, minWidth:14, textAlign:"center" }}>
                  {val}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   6. PHOTO UPLOAD
═══════════════════════════════════════════════ */
function PhotoUpload({ photos, onAdd, onRemove }) {
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const { isMobile } = useBreakpoint();

  const processFiles = (files) => {
    Array.from(files).slice(0, 6 - photos.length).forEach(file => {
      if (!file.type.startsWith("image/")) return;
      const url = URL.createObjectURL(file);
      onAdd({ url, name: file.name, size: (file.size / 1024).toFixed(0) + " KB", file });
    });
  };

  const gridColumns = isMobile ? "repeat(3, 1fr)" : "repeat(6, 1fr)";

  return (
    <div>
      <div
        className={`upload-zone${drag ? " dragging" : ""}`}
        style={{ padding: isMobile ? "24px 20px" : "32px 24px", textAlign:"center", marginBottom:photos.length > 0 ? 14 : 0 }}
        onClick={() => fileRef.current.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); processFiles(e.dataTransfer.files); }}
      >
        <div style={{
          width: isMobile ? 40 : 50, height: isMobile ? 40 : 50, borderRadius:14,
          background: drag ? T.greenTint : "#F3F4F6",
          border:`1.5px solid ${drag ? T.green : T.border}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          margin:"0 auto 14px", transition:"all 0.2s",
        }}>
          <Icon d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" d2="M17 8l-5-5-5 5M12 3v12"
            size={isMobile ? 18 : 22} stroke={drag ? T.green : T.textMuted} sw={2}/>
        </div>
        <p style={{ fontSize: isMobile ? 13 : 14, fontWeight:700, color: drag ? T.green : T.text, margin:"0 0 5px", fontFamily:T.font }}>
          {drag ? "Release to upload" : "Add photos from your visit"}
        </p>
        <p style={{ fontSize: isMobile ? 11 : 12, color:T.textMuted, margin:0, fontFamily:T.font }}>
          Drag & drop or click to select · JPG, PNG · Max 6 photos
        </p>
      </div>

      <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:"none" }} onChange={e => processFiles(e.target.files)}/>

      {photos.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns: gridColumns, gap:8 }}>
          {photos.map((p, i) => (
            <div key={i} style={{
              position:"relative", aspectRatio:"1",
              borderRadius:T.rSm, overflow:"hidden",
              border:`1.5px solid ${T.border}`,
              boxShadow:T.shadowXs,
              animation:"rvFadeIn 0.3s ease both",
            }}>
              <img src={p.url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
              <button onClick={() => onRemove(i)} style={{
                position:"absolute", top:4, right:4, width:20, height:20, borderRadius:"50%",
                background:"rgba(0,0,0,0.60)", border:"none", color:"#fff",
                fontSize:9, cursor:"pointer", display:"flex", alignItems:"center",
                justifyContent:"center", padding:0, lineHeight:1,
                minHeight: isMobile ? tokens.spacing[44] : "auto",
              }}>✕</button>
            </div>
          ))}
          {photos.length < 6 && (
            <div onClick={() => fileRef.current.click()} style={{
              aspectRatio:"1", borderRadius:T.rSm,
              border:`2px dashed ${T.border}`,
              display:"flex", alignItems:"center", justifyContent:"center",
              cursor:"pointer", color:T.textMuted,
              fontSize: isMobile ? 18 : 20, fontWeight:400, transition:"all 0.15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.green; e.currentTarget.style.color = T.green; e.currentTarget.style.background = T.greenTint; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; e.currentTarget.style.background = "none"; }}>
              +
            </div>
          )}
        </div>
      )}

      {photos.length === 0 && (
        <div style={{ marginTop:14 }}>
          <p style={{ fontSize: isMobile ? 10 : 11, color:T.textMuted, margin:"0 0 8px", fontFamily:T.font, fontWeight:500 }}>
            Example photos from other clients:
          </p>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {["/images/Reviews/upload-1.png", "/images/Reviews/upload-2.png", "/images/Reviews/upload-3.png"].map((src, i) => (
              <div key={i} style={{ width: isMobile ? 56 : 68, height: isMobile ? 56 : 68, borderRadius:T.rSm, overflow:"hidden", border:`1px solid ${T.border}`, flexShrink:0 }}>
                <RvImg src={src} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}
                  fallbackBg={["#F0FDF4","#FEF3C7","#EFF6FF"][i]}
                  fallbackContent={["🧹","🪣","🏡"][i]}/>
              </div>
            ))}
            <p style={{ fontSize: isMobile ? 10 : 11, color:T.textMuted, fontFamily:T.font, alignSelf:"center", marginLeft:4, lineHeight:1.5 }}>
              Photos help future<br/>clients see your work.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   7. REVIEW COMPOSER
═══════════════════════════════════════════════ */
function ReviewComposer({ state, dispatch, onSubmit, submitState }) {
  const textRef = useRef(null);
  const { isMobile } = useBreakpoint();
  const cardPadding = isMobile ? "24px 24px 20px" : "36px 36px 32px";
  const titleFontSize = isMobile ? 18 : 20;

  const appendChip = (text) => {
    const cur = state.text;
    const sep = cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "";
    const next = cur + sep + text + " ";
    dispatch({ type:"SET_TEXT", payload:next });
    setTimeout(() => textRef.current?.focus(), 0);
  };

  const canSubmit   = state.rating > 0 && state.text.trim().length >= 10;
  const charPct     = Math.min(100, (state.text.length / 1000) * 100);
  const charNearEnd = state.text.length > 900;

  return (
    <div className="rv-surface-raised" style={{ padding: cardPadding, animation:"rvFadeUp 0.5s ease 0.1s both" }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:28, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ fontSize: titleFontSize, fontWeight:800, color:T.text, margin:"0 0 4px", fontFamily:T.font, letterSpacing:"-0.02em" }}>
            Write your review
          </h2>
          {!isMobile && (
            <p style={{ fontSize:13, color:T.textMuted, margin:0, fontFamily:T.font }}>
              Share what made this booking memorable.
            </p>
          )}
        </div>
        {state.draftSaved && (
          <span style={{ fontSize: isMobile ? 10 : 11, fontWeight:600, color:T.green, fontFamily:T.font, background:T.greenTint, border:`1px solid ${T.greenBd}`, borderRadius:20, padding:"4px 10px", display:"flex", alignItems:"center", gap:5, animation:"rvFadeIn 0.2s ease both" }}>
            <Check size={9} color={T.green}/>Draft saved
          </span>
        )}
      </div>

      <div style={{ marginBottom:28 }}>
        <p style={{ fontSize: isMobile ? 10 : 11, fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", margin:"0 0 12px", fontFamily:T.font }}>
          Overall rating
        </p>
        <StarRating rating={state.rating} onRate={r => dispatch({ type:"SET_RATING", payload:r })}/>
      </div>

      <div className="rv-divider" style={{ marginBottom:24 }}/>

      <div style={{ marginBottom:28 }}>
        <p style={{ fontSize: isMobile ? 10 : 11, fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", margin:"0 0 4px", fontFamily:T.font }}>
          Rate specific aspects
        </p>
        <p style={{ fontSize: isMobile ? 11 : 12, color:T.textMuted, margin:"0 0 14px", fontFamily:T.font }}>Optional; helps clients understand different dimensions of the service.</p>
        <CategoryRatings values={state.categories} onChange={(label, val) => dispatch({ type:"SET_CATEGORY", label, val })}/>
      </div>

      <div className="rv-divider" style={{ marginBottom:24 }}/>

      <div style={{ marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:10, flexWrap:"wrap", gap:8 }}>
          <p style={{ fontSize: isMobile ? 10 : 11, fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", margin:0, fontFamily:T.font }}>
            Your review
          </p>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width: isMobile ? 50 : 60, height:3, background:T.borderSub, borderRadius:99, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${charPct}%`, background: charNearEnd ? "#EF4444" : T.green, borderRadius:99, transition:"width 0.2s, background 0.2s" }}/>
            </div>
            <span style={{ fontSize: isMobile ? 10 : 11, color: charNearEnd ? "#EF4444" : T.textMuted, fontFamily:T.font }}>
              {state.text.length}/1000
            </span>
          </div>
        </div>
        <textarea
          ref={textRef}
          value={state.text}
          onChange={e => dispatch({ type:"SET_TEXT", payload:e.target.value.slice(0, 1000) })}
          placeholder="Tell us what stood out. What should other clients know? Share details about the quality, timeliness, and overall experience…"
          rows={isMobile ? 5 : 6}
          style={{
            width:"100%", padding: isMobile ? "12px 14px" : "16px 18px", borderRadius:T.rMd,
            border:`1.5px solid ${T.border}`, fontSize: isMobile ? 13 : 14, color:T.text,
            outline:"none", fontFamily:T.font, lineHeight:1.65,
            transition:"border-color 0.15s, box-shadow 0.15s",
            boxSizing:"border-box", background:"#FAFBFA",
            minHeight: isMobile ? tokens.spacing[44] : "auto",
          }}
          onFocus={e => { e.target.style.borderColor = T.green; e.target.style.boxShadow = T.greenGlow; e.target.style.background = "#fff"; }}
          onBlur={e  => { e.target.style.borderColor = T.border; e.target.style.boxShadow = "none"; e.target.style.background = "#FAFBFA"; }}
        />
      </div>

      <div style={{ marginBottom:28 }}>
        <p style={{ fontSize: isMobile ? 10 : 11, color:T.textMuted, margin:"0 0 9px", fontFamily:T.font, fontWeight:500 }}>
          Quick suggestions; click to add:
        </p>
        <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
          {PROMPT_CHIPS.map(chip => {
            const active = state.text.includes(chip);
            return (
              <button key={chip} className={`chip${active ? " chip--active" : ""}`} onClick={() => appendChip(chip)} style={{ minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
                {active && <Check size={9} color={T.green}/>}
                {chip}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rv-divider" style={{ marginBottom:24 }}/>

      <div style={{ marginBottom:28 }}>
        <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:14 }}>
          <p style={{ fontSize: isMobile ? 10 : 11, fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", margin:0, fontFamily:T.font }}>
            Add photos
          </p>
          <span style={{ fontSize: isMobile ? 10 : 11, color:T.textMuted, fontFamily:T.font, fontWeight:400 }}>optional</span>
        </div>
        <PhotoUpload
          photos={state.photos}
          onAdd={p => dispatch({ type:"ADD_PHOTO", payload:p })}
          onRemove={i => dispatch({ type:"REMOVE_PHOTO", payload:i })}
        />
      </div>

      <div className="rv-divider" style={{ marginBottom:24 }}/>

      <div style={{ marginBottom:32 }}>
        <p style={{ fontSize: isMobile ? 10 : 11, fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", margin:"0 0 4px", fontFamily:T.font }}>
          Leave a tip
        </p>
        <p style={{ fontSize: isMobile ? 11 : 12, color:T.textMuted, margin:"0 0 12px", fontFamily:T.font }}>
          Entirely optional; a small token of appreciation for exceptional work.
        </p>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {TIP_OPTIONS.map(opt => (
            <button key={opt.value}
              className={`tip-pill${state.tip === opt.value ? " tip-pill--active" : ""}`}
              onClick={() => dispatch({ type:"SET_TIP", payload:opt.value })}
              style={{ minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
        <button className="rv-btn-primary" onClick={onSubmit}
          disabled={!canSubmit || submitState === "submitting"}
          style={{ height: isMobile ? 48 : 52, padding: isMobile ? "0 28px" : "0 36px", fontSize: isMobile ? 14 : 15, minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
          {submitState === "submitting" ? (
            <>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                style={{ animation:"rvSpin 0.7s linear infinite" }}>
                <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
              </svg>
              Submitting…
            </>
          ) : (
            <>
              <Icon d="M22 2L11 13" d2="M22 2L15 22l-4-9-9-4 20-7z" size={15} stroke="white" sw={2.5}/>
              Submit Review
            </>
          )}
        </button>
        <button className="rv-btn-ghost" style={{ height: isMobile ? 48 : 52, padding: isMobile ? "0 20px" : "0 24px", minHeight: isMobile ? tokens.spacing[44] : "auto" }}
          onClick={() => dispatch({ type:"SAVE_DRAFT" })}>
          {state.draftSaved ? (
            <><Check size={12} color={T.green}/> Draft saved</>
          ) : "Save Draft"}
        </button>
        {!canSubmit && (
          <p style={{ fontSize: isMobile ? 11 : 12, color:T.textMuted, margin:0, fontFamily:T.font, fontStyle:"italic" }}>
            {state.rating === 0 ? "Add a star rating to continue." : "Write at least 10 characters."}
          </p>
        )}
      </div>

      {submitState === "error" && (
        <div style={{ marginTop:14, padding:"10px 14px", borderRadius:T.rSm, background:T.errorTint, border:`1px solid ${T.errorBd}`, display:"flex", alignItems:"center", gap:9 }}>
          <Icon d="M12 8v4M12 16h.01" size={15} stroke={T.error} sw={2}/>
          <p style={{ fontSize: isMobile ? 11 : 12, color:T.error, margin:0, fontFamily:T.font, fontWeight:600 }}>
            Something went wrong. Your draft is saved. Please try again.
          </p>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   8. LIVE REVIEW PREVIEW — revised
   - Reviewer name/avatar from profiles
   - Verified Booking badge when job.status === 'completed'
     AND job.user_id === reviewer.id
   - Pro Reply block (read-only) when an existing review has pro_reply
   - Service label from job.description
═══════════════════════════════════════════════ */
function ReviewPreview({ state, job, reviewer, existingReview }) {
  const { isMobile } = useBreakpoint();
  const avgCat = Object.values(state.categories).length >= 1
    ? (Object.values(state.categories).reduce((a, b) => a + b, 0) / Object.values(state.categories).length).toFixed(1)
    : null;
  const cardPadding = isMobile ? "20px 22px" : "24px 26px";
  const avatarSize = isMobile ? 36 : 40;
  const titleFontSize = isMobile ? 13 : 14;

  const displayName = reviewer?.full_name || reviewer?.email?.split('@')[0] || "You";
  const firstName = displayName.split(" ")[0];
  const initial = displayName.trim().charAt(0).toUpperCase() || "Y";

  const isVerified = job?.status === "completed" && job?.user_id && reviewer?.id && job.user_id === reviewer.id;
  const serviceLabel = job?.description || "Service";

  return (
    <div className="rv-surface" style={{ padding: cardPadding, animation:"rvFadeUp 0.5s ease 0.16s both" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ width:7, height:7, borderRadius:"50%", background:T.green, animation:"rvPulse 2.5s ease-in-out infinite", display:"inline-block" }}/>
          <p style={{ fontSize: isMobile ? 10 : 11, fontWeight:700, color:T.textMuted, margin:0, fontFamily:T.font, textTransform:"uppercase", letterSpacing:"0.09em" }}>
            Public review preview
          </p>
        </div>
        {isVerified && <VerifiedBadge/>}
      </div>

      <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:14, flexWrap:"wrap" }}>
        <div style={{ width: avatarSize, height: avatarSize, borderRadius:"50%", overflow:"hidden", flexShrink:0, border:`1.5px solid ${T.border}` }}>
          <RvImg
            src={reviewer?.avatar_url}
            alt={firstName}
            style={{ width:"100%", height:"100%", objectFit:"cover" }}
            fallbackBg="linear-gradient(135deg,#dbeafe,#3b82f6)"
            fallbackContent={<span style={{ fontSize: isMobile ? 13 : 15, fontWeight:700, color:"#fff" }}>{initial}</span>}
          />
        </div>
        <div style={{ flex:1 }}>
          <p style={{ fontSize: titleFontSize, fontWeight:700, color:T.text, margin:"0 0 3px", fontFamily:T.font }}>{firstName}{firstName !== displayName ? " " + displayName.split(" ")[1]?.charAt(0) + "." : ""}</p>
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            {[1,2,3,4,5].map(i => (
              <svg key={i} width="13" height="13" viewBox="0 0 24 24"
                fill={state.rating >= i ? T.goldBright : T.borderSub} stroke="none">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            ))}
            {state.rating > 0 && (
              <span style={{ fontSize: isMobile ? 10 : 11, fontWeight:700, color:T.gold, fontFamily:T.font, marginLeft:3 }}>
                {state.rating}.0
              </span>
            )}
          </div>
        </div>
        <span style={{ fontSize: isMobile ? 10 : 11, color:T.textMuted, fontFamily:T.font, flexShrink:0 }}>Just now</span>
      </div>

      <p style={{
        fontSize: isMobile ? 13 : 14, color: state.text.trim() ? T.text : T.textMuted,
        margin:"0 0 14px", fontFamily:T.font, lineHeight:1.7,
        fontStyle: state.text.trim() ? "normal" : "italic",
        minHeight: 42,
      }}>
        {state.text.trim()
          ? (state.text.length > 200 ? state.text.slice(0, 200) + "…" : state.text)
          : "Your review will appear here as you type…"}
      </p>

      {state.photos.length > 0 && (
        <div style={{ display:"flex", gap:7, marginBottom:14, flexWrap:"wrap" }}>
          {state.photos.slice(0,4).map((p, i) => (
            <div key={i} style={{ width: isMobile ? 48 : 58, height: isMobile ? 48 : 58, borderRadius:9, overflow:"hidden", flexShrink:0, boxShadow:T.shadowXs }}>
              <img src={p.url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
            </div>
          ))}
          {state.photos.length > 4 && (
            <div style={{ width: isMobile ? 48 : 58, height: isMobile ? 48 : 58, borderRadius:9, background:T.bg, border:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize: isMobile ? 10 : 11, color:T.textMuted, fontFamily:T.font, fontWeight:700 }}>
              +{state.photos.length - 4}
            </div>
          )}
        </div>
      )}

      <div style={{ paddingTop:12, borderTop:`1px solid ${T.borderSub}`, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
        <span style={{ fontSize: isMobile ? 11 : 12, fontWeight:600, color:T.textSub, fontFamily:T.font }}>{serviceLabel}</span>
        {avgCat && (
          <span style={{ fontSize: isMobile ? 10 : 11, color:T.textMuted, fontFamily:T.font }}>
            · {avgCat}★ avg across {Object.values(state.categories).length} categories
          </span>
        )}
        {state.tip > 0 && (
          <span style={{ fontSize: isMobile ? 10 : 11, color:T.gold, fontWeight:600, fontFamily:T.font, marginLeft:"auto" }}>
            +KSh {state.tip} tip
          </span>
        )}
      </div>

      <ProReplyBlock
        reply={existingReview?.pro_reply}
        replyAt={existingReview?.pro_reply_at}
        proName={job?.pro_profiles?.full_name}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   9. GUIDANCE SECTION — revised
   Curated, hardcoded premium imagery representing real
   service categories (replaces random/dynamic guide images)
═══════════════════════════════════════════════ */
function GuidanceSection() {
  const { isMobile, isTablet } = useBreakpoint();
  const guides = [
    {
      img:    "/images/Reviews/guide-home-cleaning.jpg",
      fallbackBg: "linear-gradient(145deg,#dcfce7,#86efac)",
      fallbackIco: "✍️",
      title:  "Be honest",
      body:   "Share your genuine experience. Accurate reviews help the entire community and protect clients who come after you.",
    },
    {
      img:    "/images/Reviews/guide-electrical.jpg",
      fallbackBg: "linear-gradient(145deg,#fef3c7,#fde68a)",
      fallbackIco: "🔍",
      title:  "Be specific",
      body:   "Mention exactly what the JiraniPro did well or what could be improved. Specifics are far more useful than vague praise.",
    },
    {
      img:    "/images/Reviews/guide-landscaping.jpg",
      fallbackBg: "linear-gradient(145deg,#dbeafe,#93c5fd)",
      fallbackIco: "📸",
      title:  "Share useful details",
      body:   "Small observations help others book with confidence. Photos, timing, communication style every detail counts.",
    },
  ];

  const gridColumns = isMobile ? "1fr" : (isTablet ? "repeat(2, 1fr)" : "repeat(3,1fr)");
  const titleFontSize = isMobile ? 16 : 18;

  return (
    <div style={{ animation:"rvFadeUp 0.5s ease 0.22s both" }}>
      <div style={{ display:"flex", alignItems:"baseline", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <h2 style={{ fontSize: titleFontSize, fontWeight:800, color:T.text, margin:0, fontFamily:T.font, letterSpacing:"-0.02em" }}>
          Writing a great review
        </h2>
        <p style={{ fontSize: isMobile ? 12 : 13, color:T.textMuted, margin:0, fontFamily:T.font }}>[a few tips]</p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns: gridColumns, gap:16 }}>
        {guides.map((g, i) => (
          <div key={i} className="guide-card">
            <div style={{ height: isMobile ? 100 : 120, overflow:"hidden", borderRadius:`${T.r} ${T.r} 0 0`, position:"relative" }}>
              <RvImg
                src={g.img || ""}
                alt=""
                style={{ width:"100%", height:"100%", objectFit:"cover" }}
                fallbackBg={g.fallbackBg}
                fallbackContent={<span style={{ fontSize: isMobile ? 36 : 44 }}>{g.fallbackIco}</span>}
              />
            </div>
            <div style={{ padding: isMobile ? "14px 14px 12px" : "18px 18px 16px" }}>
              <p style={{ fontSize: isMobile ? 13 : 14, fontWeight:800, color:T.text, margin:"0 0 6px", fontFamily:T.font }}>{g.title}</p>
              <p style={{ fontSize: isMobile ? 12 : 13, color:T.textSub, margin:0, fontFamily:T.font, lineHeight:1.6 }}>{g.body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   10. BOOKING SIDEBAR — revised
   - Pro name/avatar/rating from job.pro_profiles
   - Service/date/location/price from job_requests
   - Ref # from job.id (uuid, shortened for display)
═══════════════════════════════════════════════ */
function BookingSidebar({ job }) {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const cardPadding = isMobile ? "0 16px 16px" : "0 20px 20px";

  const handleViewBooking = () => navigate("/bookings");
  const handleMessageSupport = () => navigate("/support");
  const handleRebook = () => navigate("/search");

  const pro = job?.pro_profiles || {};
  const proName = pro.full_name || "Your JiraniPro";
  const proInitial = proName.trim().charAt(0).toUpperCase() || "P";
  const proRating = pro.rating != null ? Number(pro.rating).toFixed(1) : "—";
  const proJobs = pro.total_jobs != null ? `${pro.total_jobs} reviews` : "";
  const ratingFilled = pro.rating != null ? Math.round(Number(pro.rating)) : 0;

  const serviceLabel = job?.description || "Service";
  const formattedDate = job?.scheduled_time
    ? new Date(job.scheduled_time).toLocaleString("en-GB", {
        weekday:"short", day:"numeric", month:"short", year:"numeric", hour:"numeric", minute:"2-digit",
      })
    : "—";
  const formattedPrice = job?.estimated_price != null
    ? `KSh ${Number(job.estimated_price).toLocaleString()}`
    : "—";
  const refNumber = job?.id ? `JR-${job.id.slice(0, 8).toUpperCase()}` : "—";
  const isCompleted = job?.status === "completed";

  return (
    <div style={{ position: isMobile ? "relative" : "sticky", top: isMobile ? 0 : 76, display:"flex", flexDirection:"column", gap:14 }}>

      <div className="rv-surface-raised" style={{ overflow:"hidden" }}>
        <div style={{ height: isMobile ? 50 : 60, position:"relative", overflow:"hidden", background:"#F0F7F1" }}>
          <RvImg
            src="/images/Reviews/review-hero-3.png"
            alt=""
            style={{ width:"100%", height:"100%", objectFit:"cover", opacity:0.55 }}
            fallbackBg="linear-gradient(135deg,#d1fae5,#6ee7b7)"
            fallbackContent=""
          />
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.30) 100%)" }}/>
        </div>

        <div style={{ padding: cardPadding, position:"relative", marginTop: -22 }}>
          <div style={{
            width: isMobile ? 40 : 44, height: isMobile ? 40 : 44, borderRadius:"50%", overflow:"hidden",
            border:"3px solid #fff", boxShadow:T.shadowXs, marginBottom:10,
          }}>
            <RvImg
              src={pro.avatar_url}
              alt={proName}
              style={{ width:"100%", height:"100%", objectFit:"cover" }}
              fallbackBg="linear-gradient(145deg,#d1fae5,#16a34a)"
              fallbackContent={<span style={{ fontSize: isMobile ? 14 : 16, fontWeight:700, color:"#fff" }}>{proInitial}</span>}
            />
          </div>
          <p style={{ fontSize: isMobile ? 14 : 15, fontWeight:800, color:T.text, margin:"0 0 2px", fontFamily:T.font }}>{proName}</p>
          <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:14 }}>
            <div style={{ display:"flex", gap:2 }}>
              {[0,1,2,3,4].map(i => <StarSvg key={i} filled={i < ratingFilled} size={isMobile ? 10 : 11} color={i < ratingFilled ? undefined : "#E5E7EB"}/>)}
            </div>
            <span style={{ fontSize: isMobile ? 10 : 11, color:T.textMuted, fontFamily:T.font }}>{proRating}{proJobs ? ` · ${proJobs}` : ""}</span>
            {pro.is_verified && (
              <span style={{ fontSize: isMobile ? 9 : 10, fontWeight:700, color:T.green, fontFamily:T.font, display:"inline-flex", alignItems:"center", gap:3 }}>
                <Check size={9} color={T.green}/>Verified
              </span>
            )}
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
            {[
              { icon:"M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01", label:"Service",    val:serviceLabel },
              { icon:"M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z", label:"Date",       val:formattedDate },
              { icon:"M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",        label:"Location",   val: job?.location || "—" },
              { icon:"M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6", label:"Total paid", val:formattedPrice },
              { icon:"M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2", label:"Ref #", val:refNumber },
            ].map(({ icon, label, val }) => (
              <div key={label} style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                <Icon d={icon} size={isMobile ? 12 : 13} stroke={T.textMuted} sw={2}/>
                <div>
                  <p style={{ fontSize: isMobile ? 9 : 10, color:T.textMuted, margin:"0 0 1px", fontFamily:T.font }}>{label}</p>
                  <p style={{ fontSize: isMobile ? 11 : 12, fontWeight:700, color:T.text, margin:0, fontFamily:T.font }}>{val}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop:14, display:"flex", alignItems:"center", gap:7, padding:"8px 12px",
            background: isCompleted ? T.greenTint : "#F3F4F6",
            border:`1px solid ${isCompleted ? T.greenBd : T.border}`, borderRadius:9 }}>
            <span style={{ width:7, height:7, borderRadius:"50%", background: isCompleted ? T.green : T.textMuted, flexShrink:0 }}/>
            <span style={{ fontSize: isMobile ? 11 : 12, fontWeight:700, color: isCompleted ? T.green : T.textMuted, fontFamily:T.font, textTransform:"capitalize" }}>
              {isCompleted ? "Completed successfully" : (job?.status || "Unknown status")}
            </span>
          </div>
        </div>
      </div>

      <div className="rv-surface" style={{ padding:"16px 18px" }}>
        <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
          <div style={{ width:34, height:34, borderRadius:10, background:T.goldLight, border:`1px solid ${T.goldBd}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <StarSvg filled size={16} color={T.gold}/>
          </div>
          <div>
            <p style={{ fontSize: isMobile ? 12 : 13, fontWeight:700, color:T.text, margin:"0 0 4px", fontFamily:T.font }}>Your review matters</p>
            <p style={{ fontSize: isMobile ? 11 : 12, color:T.textMuted, margin:0, fontFamily:T.font, lineHeight:1.6 }}>
              Honest reviews help future clients choose the right JiraniPro and reward quality work.
            </p>
          </div>
        </div>
      </div>

      <div className="rv-surface" style={{ padding:"16px 18px" }}>
        <p style={{ fontSize: isMobile ? 9 : 10, fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.09em", margin:"0 0 10px", fontFamily:T.font }}>
          Booking actions
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
          {[
            { icon:"M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8", icon2:"M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z", label:"View booking", onClick: handleViewBooking },
            { icon:"M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z", label:"Message support", onClick: handleMessageSupport },
            { icon:"M1 4v6h6M23 20v-6h-6", icon2:"M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15", label:"Rebook this service", onClick: handleRebook },
          ].map(({ icon, icon2, label, onClick }) => (
            <button key={label} className="sidebar-action" onClick={onClick} style={{ minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
              <svg width={isMobile ? 12 : 13} height={isMobile ? 12 : 13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d={icon}/>{icon2 && <path d={icon2}/>}
              </svg>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   11. SUCCESS OVERLAY
═══════════════════════════════════════════════ */
function SuccessScreen({ onClose }) {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const handleViewBookings = () => {
    navigate("/bookings");
    if (onClose) onClose();
  };

  const handleBackHome = () => {
    navigate("/");
    if (onClose) onClose();
  };

  return (
    <div style={{
      position:"fixed", inset:0,
      background:"rgba(249,250,251,0.96)",
      backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
      zIndex:600, display:"flex", alignItems:"center", justifyContent:"center",
      animation:"rvFadeIn 0.3s ease both",
    }}>
      <div style={{ maxWidth: isMobile ? "90%" : 520, width:"90%", textAlign:"center", padding:"0 24px" }}>
        <div style={{ position:"relative", width: isMobile ? 100 : 140, height: isMobile ? 100 : 140, margin:"0 auto 28px" }}>
          <div style={{
            position:"absolute", inset:0, borderRadius:"50%",
            background:T.greenTint, border:`2px solid ${T.greenBd}`,
            animation:"rvSuccess 0.55s cubic-bezier(0.22,1,0.36,1) both",
          }}/>
          <div style={{
            position:"absolute", inset:10, borderRadius:"50%",
            overflow:"hidden", boxShadow:T.shadowMd,
          }}>
            <RvImg
              src="/images/Reviews/success-1.png"
              alt="Review submitted"
              style={{ width:"100%", height:"100%", objectFit:"cover" }}
              fallbackBg="linear-gradient(145deg,#dcfce7,#86efac)"
              fallbackContent={<span style={{ fontSize: isMobile ? 40 : 52 }}>🏡</span>}
            />
            <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(22,163,74,0.55)" }}>
              <svg width={isMobile ? 36 : 44} height={isMobile ? 36 : 44} viewBox="0 0 20 20" fill="white">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
        </div>

        <div style={{ display:"inline-flex", alignItems:"center", gap:7, background:T.greenTint, border:`1px solid ${T.greenBd}`, borderRadius:99, padding:"5px 16px", marginBottom:20 }}>
          <span style={{ width:7, height:7, borderRadius:"50%", background:T.green, display:"block" }}/>
          <span style={{ fontSize: isMobile ? 11 : 12, fontWeight:700, color:T.green, fontFamily:T.font }}>Pending moderation</span>
        </div>

        <h2 style={{ fontSize: isMobile ? 28 : 32, fontWeight:800, color:T.text, margin:"0 0 12px", fontFamily:T.font, letterSpacing:"-0.03em" }}>
          Review submitted
        </h2>
        <p style={{ fontSize: isMobile ? 14 : 15, color:T.textSub, margin:"0 0 32px", fontFamily:T.font, lineHeight:1.7, maxWidth:400, marginLeft:"auto", marginRight:"auto" }}>
          Thank you for sharing your experience. Your review will be published after moderation usually within a few hours.
        </p>

        <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
          <button className="rv-btn-primary" onClick={handleViewBookings} style={{ height: isMobile ? 44 : 50, padding: isMobile ? "0 24px" : "0 30px", fontSize: isMobile ? 13 : 14, minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
            View my bookings
          </button>
          <button className="rv-btn-ghost" onClick={handleBackHome} style={{ height: isMobile ? 44 : 50, padding: isMobile ? "0 20px" : "0 24px", minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   12. FOOTER — revised
   - AppLogo replaces inline wordmark
   - "Contact" → "Help Center" linking to /help
═══════════════════════════════════════════════ */
function Footer() {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const handleNav = (path) => navigate(path);

  const links = [
    { label:"Privacy",     path:"/privacy" },
    { label:"Terms",       path:"/terms" },
    { label:"Help Center", path:"/help" },
  ];

  return (
    <footer style={{ background:T.card, borderTop:`1px solid ${T.border}`, padding: isMobile ? "20px 24px" : "28px 36px", marginTop: isMobile ? 40 : 56 }}>
      <div style={{ maxWidth:1120, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 16 : 0 }}>
        <AppLogo size={isMobile ? 14 : 16} onClick={() => navigate("/")} style={{ minHeight: isMobile ? tokens.spacing[44] : "auto" }}/>
        <div style={{ display:"flex", gap: isMobile ? 16 : 24, flexWrap:"wrap", justifyContent:"center" }}>
          {links.map(l => (
            <button key={l.label} onClick={() => handleNav(l.path)} style={{ background:"none", border:"none", fontSize: isMobile ? 12 : 13, color:T.textMuted, fontFamily:T.font, cursor:"pointer", padding:0, transition:"color 0.15s", minHeight: isMobile ? tokens.spacing[44] : "auto" }}
              onMouseEnter={e => e.currentTarget.style.color = T.text}
              onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>
              {l.label}
            </button>
          ))}
        </div>
        <p style={{ fontSize: isMobile ? 11 : 12, color:T.textMuted, margin:0, fontFamily:T.font }}>© 2026 JiraniApp. All rights reserved.</p>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════
   STATE REDUCER
═══════════════════════════════════════════════ */
function reviewReducer(state, action) {
  switch (action.type) {
    case "SET_RATING":   return { ...state, rating:    action.payload, draftSaved:false };
    case "SET_TEXT":     return { ...state, text:      action.payload, draftSaved:false };
    case "SET_CATEGORY": return { ...state, categories:{ ...state.categories, [action.label]:action.val }, draftSaved:false };
    case "ADD_PHOTO":    return { ...state, photos:    [...state.photos, action.payload] };
    case "REMOVE_PHOTO": return { ...state, photos:    state.photos.filter((_, i) => i !== action.payload) };
    case "SET_TIP":      return { ...state, tip:       action.payload };
    case "SAVE_DRAFT":   return { ...state, draftSaved:true };
    default:             return state;
  }
}

const INIT = {
  rating:     0,
  text:       "",
  categories: {},
  photos:     [],
  tip:        0,
  draftSaved: false,
};

/* ═══════════════════════════════════════════════
   ROOT — export default
   Route: /review/:jobRequestId
   FK target resolved: job_requests (uuid-based, pro-linked).
   `bookings` (integer-keyed legacy table) is not used here.
═══════════════════════════════════════════════ */
export default function ReviewPage() {
  const [state, dispatch]       = useReducer(reviewReducer, INIT);
  const [submitState, setSubmit] = useState("idle");
  const [showSuccess, setShowSuccess] = useState(false);

  const [job, setJob]               = useState(null);
  const [reviewer, setReviewer]     = useState(null);
  const [existingReview, setExisting] = useState(null);
  const [pageState, setPageState]   = useState("loading"); // loading | ready | error | already-reviewed
  const [pageError, setPageError]   = useState(null);

  const { jobRequestId } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const loadData = async () => {
    setPageState("loading");
    setPageError(null);
    try {
      const [jobData, reviewerData, existingReviewData] = await Promise.all([
        fetchJobForReview(jobRequestId),
        fetchReviewerProfile(),
        fetchExistingReview(jobRequestId),
      ]);

      // Auth guard: redirect to login if reviewer profile is null (user not logged in)
      if (!reviewerData) {
        navigate('/login');
        return;
      }

      setJob(jobData);
      setReviewer(reviewerData);
      setExisting(existingReviewData);

      if (existingReviewData) {
        // Pre-populate composer with existing review (read-only view via preview + pro reply)
        dispatch({ type:"SET_RATING", payload: existingReviewData.rating });
        dispatch({ type:"SET_TEXT", payload: existingReviewData.review_text || "" });
        if (existingReviewData.category_ratings) {
          Object.entries(existingReviewData.category_ratings).forEach(([label, val]) => {
            dispatch({ type:"SET_CATEGORY", label, val });
          });
        }
        setPageState("already-reviewed");
      } else {
        setPageState("ready");
      }
    } catch (err) {
      setPageError(err?.message || "Something went wrong while loading this booking.");
      setPageState("error");
    }
  };

  useEffect(() => {
    if (jobRequestId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobRequestId]);

  const progressStep =
    state.rating > 0
      ? state.text.trim().length > 9
        ? state.photos.length > 0 ? 3 : 2
        : 1
      : 0;

  const handleSubmit = async () => {
    if (state.rating === 0 || state.text.trim().length < 10) return;
    if (!job?.pro_id) {
      setSubmit("error");
      return;
    }
    setSubmit("submitting");

    // Upload photos to Supabase Storage before creating review
    let uploadedPhotoUrls = [];
    try {
      uploadedPhotoUrls = await Promise.all(
        state.photos.map(async (photo, i) => {
          if (!photo.file) return photo.url; // fallback if no raw file (shouldn't happen for new uploads)
          const ext = photo.file.name.split('.').pop();
          const path = `reviews/${jobRequestId}/${Date.now()}-${i}.${ext}`;
          // TODO: ensure 'review-photos' storage bucket exists and has appropriate public-read policy
          const { data, error } = await supabase.storage
            .from('review-photos')
            .upload(path, photo.file, { cacheControl: '3600', upsert: false });
          if (error) throw error;
          const { data: urlData } = supabase.storage.from('review-photos').getPublicUrl(data.path);
          return urlData.publicUrl;
        })
      );
    } catch (uploadErr) {
      console.error('Photo upload error:', uploadErr);
      setSubmit("error");
      dispatch({ type: "SAVE_DRAFT" });
      return;
    }

    try {
      await createReview({
        jobRequestId:     jobRequestId,
        proId:            job.pro_id,
        rating:           state.rating,
        categoryRatings:  state.categories,
        text:             state.text.trim(),
        photos:           uploadedPhotoUrls,
        tip:              state.tip,
      });
      setSubmit("success");
      setShowSuccess(true);
    } catch (err) {
      setSubmit("error");
      dispatch({ type: "SAVE_DRAFT" });
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
  };

  const mainPadding = isMobile ? "20px 20px 0" : "32px 36px 0";
  const innerMaxWidth = 1120;

  /* ─── Loading state ─── */
  if (pageState === "loading") {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: CSS }}/>
        <Navbar reviewer={reviewer}/>
        <PageSkeleton/>
        <Footer/>
      </>
    );
  }

  /* ─── Error state ─── */
  if (pageState === "error") {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: CSS }}/>
        <Navbar reviewer={reviewer}/>
        <PageError message={pageError} onRetry={loadData} onBack={() => navigate("/bookings")}/>
        <Footer/>
      </>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS }}/>
      {showSuccess && <SuccessScreen onClose={handleSuccessClose}/>}

      <Navbar reviewer={reviewer}/>

      <main style={{ maxWidth:1280, margin:"0 auto", padding: mainPadding }}>
        <div style={{ maxWidth: innerMaxWidth, margin:"0 auto" }}>

          <Hero job={job}/>
          <ProgressBar current={pageState === "already-reviewed" ? STEPS.length : progressStep}/>

          {pageState === "already-reviewed" && (
            <div className="rv-surface-warm" style={{ padding: isMobile ? "14px 16px" : "16px 20px", marginBottom:22, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              <div style={{
                width:30, height:30, borderRadius:9, background:T.greenTint, border:`1px solid ${T.greenBd}`,
                display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
              }}>
                <Check size={14} color={T.green}/>
              </div>
              <div>
                <p style={{ fontSize: isMobile ? 12 : 13, fontWeight:700, color:T.text, margin:"0 0 1px", fontFamily:T.font }}>
                  You've already reviewed this booking
                </p>
                <p style={{ fontSize: isMobile ? 11 : 12, color:T.textMuted, margin:0, fontFamily:T.font }}>
                  Status: {existingReview?.moderation_status === "approved" ? "Published" : existingReview?.moderation_status === "rejected" ? "Not published" : "Pending moderation"}
                </p>
              </div>
            </div>
          )}

          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "7fr 5fr", gap: isMobile ? 24 : 28, alignItems:"start" }}>
            <div style={{ display:"flex", flexDirection:"column", gap:22 }}>
              {pageState === "ready" && (
                <ReviewComposer state={state} dispatch={dispatch} onSubmit={handleSubmit} submitState={submitState}/>
              )}
              <ReviewPreview state={state} job={job} reviewer={reviewer} existingReview={existingReview}/>
              <GuidanceSection/>
            </div>
            {!isMobile && <BookingSidebar job={job}/>}
          </div>

        </div>
      </main>

      <Footer/>
    </>
  );
}